'use client'

import useAppStore from '@/core/stores/app';
import useSetSearchParams from '@/hooks/useSetSearchParams';
import useSizeController from '@/hooks/useSizeController';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
    identifier: string;
    tabletHeight?: string;
    isFull?: boolean;
    bgColor?: string;
    maxWidth?: string;
    title?: string;
    show: boolean;
    onClose: () => void;
    children: ReactNode;
    appHeight?: string
}

const Modal: React.FC<ModalProps> = ({
    show,
    identifier,
    onClose,
    children,
    title,
    maxWidth = 'md:max-w-2xl',
    bgColor = 'bg-gray-50',
    isFull,
    appHeight = 'auto',
    tabletHeight = 'md:h-fit md:max-h-[85dvh]'
}) => {
    const [isReady, setIsReady] = useState(false);
    const { isApp } = useAppStore()
    const { removeQuey, addQuey, params, setSearchParam } = useSetSearchParams();
    const { back } = useRouter();
    const { isSmall, innerWidth } = useSizeController();

    const [localShow, setLocalShow] = useState(false)


    useEffect(() => {
        setLocalShow(show)
    }, [show]);



    const formattedIdentifier = `modal-${identifier}`;


    const queryIdentifier = params[formattedIdentifier];


    useEffect(() => {
        if (innerWidth > 0 && isSmall) {
            if (show) {

                setSearchParam([{ [formattedIdentifier]: 'true' }])
            } else if (queryIdentifier) {
                back()
            }
        }

    }, [show, innerWidth])

    useEffect(() => {
        if (!queryIdentifier) {
            !!(isSmall && innerWidth > 0) && onClose();
        }
    }, [queryIdentifier]);

    useEffect(() => {
        if (typeof window !== 'undefined' && document) {
            setIsReady(true);
        }
    }, []);

    if (!isReady) {
        return null;
    }

    return isReady && createPortal(
        <AnimatePresence>
            {localShow && (
                <>
                    <motion.div
                        onClick={(e) => {
                            e.stopPropagation()
                            if (isSmall) {

                                back();
                            }
                            onClose();
                        }}
                        className="fixed bottom-0 left-0 right-0 top-0 z-[100] before:absolute before:right-0 before:top-0 before:z-10 before:h-full before:w-full before:bg-gray-700/50"
                    />
                    <motion.div
                        onClick={(e) => e.stopPropagation()}
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1, transition: { delay: 0.2 } }}
                        exit={{ y: 50, opacity: 0 }}
                        transition={{
                            duration: 0.3,
                            type: 'inertia',
                        }}
                        className={`${maxWidth} ${bgColor} ${isFull ? 'h-[100dvh] md:h-fit bg' : 'max-w-[90%]  h-fit  rounded-lg'} 
                                    app appHeight fixed bottom-0 left-0 right-0 top-0 z-[1000]  m-auto
                                    w-full  flex flex-col max-h-dvh  md:rounded-2xl no-scroller
                                    overflow-y-auto`}
                    >
                        {title && (
                            <button
                                onClick={() => {
                                    if (isSmall) {

                                        back();
                                    }
                                    onClose();
                                }}
                                className={`${bgColor} justify-items-start border-b sticky top-0 left-0 z-50 text-gray-800 text-sm font-semibold capitalize w-full p-4 grid grid-cols-[24px,_1fr] gap-2`}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} className='justify-self-center place-self-center' />
                                <span>{title}</span>
                            </button>
                        )}
                        <div className='p-4 grow '>
                            {children}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        window.document.body
    )

};

// Memoize the component to avoid re-renders unless props have changed
export default React.memo(Modal, (prevProps, nextProps) => {
    return (
        prevProps.show === nextProps.show &&
        prevProps.identifier === nextProps.identifier &&
        prevProps.onClose === nextProps.onClose &&
        prevProps.children === nextProps.children &&
        prevProps.title === nextProps.title &&
        prevProps.maxWidth === nextProps.maxWidth &&
        prevProps.bgColor === nextProps.bgColor &&
        prevProps.isFull === nextProps.isFull &&
        prevProps.tabletHeight === nextProps.tabletHeight
    );
});
